import { useState, useEffect } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminService } from '../services';
import { usePermissions } from '../contexts/PermissionContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Settings, Key, Database, Activity, CheckCircle, XCircle, Palette, Webhook, Loader2, Wifi, WifiOff, ExternalLink, RefreshCw, Shield, Users, Crown, Link2, Lock, ScrollText, HardDrive, Coins, Mail, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { ThemeEditor } from '../components/ThemeEditor';
import { SSOConfigPanel, UserManagementPanel } from '../components/AdminPanels';
import { RolesPanel, UserRolesPanel } from '../components/RolesManagement';
import { SSOMapppingPanel } from '../components/SSOMapping';
import { DbLookupPanel } from '../components/admin/DbLookupPanel';
import { LogsPanel } from '../components/admin/LogsPanel';
import { BackupPanel } from '../components/admin/BackupPanel';
import { AdminCreditsPanel } from '../components/admin/AdminCreditsPanel';
import { EmailConfigPanel } from '../components/admin/EmailConfigPanel';
import { DocumentationPanel } from '../components/admin/DocumentationPanel';

export const AdminPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canAccessAdmin, hasPermission, isSuperAdmin, loading: permLoading } = usePermissions();
  const [credentials, setCredentials] = useState({
    service: 'books',
    client_id: '',
    client_secret: '',
    redirect_uri: `${process.env.REACT_APP_BACKEND_URL}/api/oauth/books/callback`
  });
  const [credentialStatus, setCredentialStatus] = useState({});
  const [oauthStatus, setOauthStatus] = useState({});
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [webhookConfig, setWebhookConfig] = useState({
    service: 'zoho_billing',
    secret: '',
    verify_signature: true
  });
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [testingService, setTestingService] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [authorizingService, setAuthorizingService] = useState(null);
  const [refreshingService, setRefreshingService] = useState(null);

  useEffect(() => {
    // Check for OAuth callback parameters
    const oauthSuccess = searchParams.get('oauth_success');
    const oauthError = searchParams.get('oauth_error');
    const service = searchParams.get('service');
    
    if (oauthSuccess === 'true' && service) {
      toast.success(`Zoho ${service.toUpperCase()} erfolgreich autorisiert!`);
      setSearchParams({});
      fetchOAuthStatus(service);
    } else if (oauthError && service) {
      toast.error(`OAuth Fehler: ${oauthError}`);
      setSearchParams({});
    }
    
    // Only fetch system config data for super-admins
    if (isSuperAdmin) {
      fetchCredentialStatus('books');
      fetchCredentialStatus('crm');
      fetchCredentialStatus('desk');
      fetchCredentialStatus('credits');
      fetchOAuthStatus('books');
      fetchOAuthStatus('crm');
      fetchOAuthStatus('desk');
      fetchOAuthStatus('credits');
      fetchSystemHealth();
      fetchWebhookStatus();
    }
  }, [isSuperAdmin]);

  const fetchOAuthStatus = async (service) => {
    try {
      const status = await adminService.getOAuthStatus(service);
      setOauthStatus(prev => ({ ...prev, [service]: status }));
    } catch (error) {
      console.error(`Error fetching OAuth status for ${service}:`, error);
    }
  };

  const fetchWebhookStatus = async () => {
    try {
      const status = await adminService.getWebhookConfig();
      setWebhookStatus(status);
    } catch (error) {
      console.error('Error fetching webhook status:', error);
    }
  };

  const fetchCredentialStatus = async (service) => {
    try {
      const status = await adminService.getCredentialStatus(service);
      setCredentialStatus(prev => ({ ...prev, [service]: status }));
    } catch (error) {
      console.error('Error fetching credential status:', error);
    }
  };

  const fetchSystemHealth = async () => {
    try {
      const health = await adminService.getSystemHealth();
      setSystemHealth(health);
    } catch (error) {
      console.error('Error fetching system health:', error);
    }
  };

  const handleConfigureCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await adminService.configureCredentials(credentials);
      toast.success(`Zoho ${credentials.service.toUpperCase()} Credentials erfolgreich konfiguriert!`);
      fetchCredentialStatus(credentials.service);
      fetchOAuthStatus(credentials.service);
      setCredentials({
        ...credentials,
        client_id: '',
        client_secret: ''
      });
    } catch (error) {
      toast.error('Fehler beim Konfigurieren der Credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async (service) => {
    setTestingService(service);
    try {
      const result = await adminService.testConnection(service);
      setTestResults(prev => ({ ...prev, [service]: result }));
      if (result.success) {
        toast.success(`Zoho ${service.toUpperCase()} Verbindung erfolgreich!`);
      } else {
        toast.error(`Verbindungstest fehlgeschlagen: ${result.message}`);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Verbindungstest fehlgeschlagen';
      setTestResults(prev => ({ ...prev, [service]: { success: false, message: errorMessage } }));
      toast.error(errorMessage);
    } finally {
      setTestingService(null);
    }
  };

  const handleAuthorizeOAuth = async (service) => {
    setAuthorizingService(service);
    try {
      const result = await adminService.initiateOAuth(service, 'eu');
      if (result.authorization_url) {
        // Redirect to Zoho authorization page
        window.location.href = result.authorization_url;
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'OAuth Autorisierung fehlgeschlagen';
      toast.error(errorMessage);
      setAuthorizingService(null);
    }
  };

  const handleRefreshToken = async (service) => {
    setRefreshingService(service);
    try {
      const result = await adminService.refreshOAuthToken(service);
      if (result.success) {
        toast.success(`Token für ${service.toUpperCase()} erfolgreich erneuert!`);
        fetchOAuthStatus(service);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Token Erneuerung fehlgeschlagen';
      toast.error(errorMessage);
    } finally {
      setRefreshingService(null);
    }
  };

  const handleConfigureWebhook = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await adminService.configureWebhook(webhookConfig);
      toast.success('Webhook-Konfiguration erfolgreich gespeichert!');
      fetchWebhookStatus();
      setWebhookConfig({
        ...webhookConfig,
        secret: ''
      });
    } catch (error) {
      toast.error('Fehler beim Konfigurieren des Webhooks');
    } finally {
      setLoading(false);
    }
  };

  return (
    permLoading ? (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-primary)' }} />
      </div>
    ) : !canAccessAdmin() ? (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center justify-center py-20 space-y-4"
      >
        <Lock className="w-16 h-16" style={{ color: 'var(--color-text-secondary)' }} />
        <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Zugriff verweigert</h2>
        <p style={{ color: 'var(--color-text-secondary)' }}>Sie haben keine Berechtigung, auf die Administration zuzugreifen.</p>
      </motion.div>
    ) : (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="admin-page"
      className="min-h-full"
      style={{ backgroundColor: 'var(--color-surface)' }}
    >
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>
          Administration
        </h1>
        <p className="mt-2" style={{ color: 'var(--color-text-secondary)' }}>Systemkonfiguration und Verwaltung</p>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        {/* Grouped tab navigation - wrapped in TabsList for proper RovingFocusGroup context */}
        <TabsList className="flex flex-wrap gap-1 sm:gap-1.5 p-1 sm:p-1.5 rounded-lg bg-slate-100 border border-slate-200 h-auto">
          {/* Allgemein */}
          <TabsTrigger value="users" data-testid="users-tab" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-xs px-3 py-1.5">
            <Users className="w-3.5 h-3.5 mr-1.5" />Benutzer
          </TabsTrigger>
          <TabsTrigger value="theme" data-testid="theme-tab" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-xs px-3 py-1.5">
            <Palette className="w-3.5 h-3.5 mr-1.5" />Theme
          </TabsTrigger>

          {isSuperAdmin && (<>
            {/* Separator */}
            <div className="w-px h-6 self-center bg-slate-300 mx-1" />

            {/* Integrationen */}
            <TabsTrigger value="credentials" data-testid="credentials-tab" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-xs px-3 py-1.5">
              <Key className="w-3.5 h-3.5 mr-1.5" />Zoho API
            </TabsTrigger>
            <TabsTrigger value="webhook" data-testid="webhook-tab" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-xs px-3 py-1.5">
              <Webhook className="w-3.5 h-3.5 mr-1.5" />Webhook
            </TabsTrigger>
            <TabsTrigger value="email" data-testid="email-tab" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-xs px-3 py-1.5">
              <Mail className="w-3.5 h-3.5 mr-1.5" />E-Mail
            </TabsTrigger>
            <TabsTrigger value="sso" data-testid="sso-tab" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-xs px-3 py-1.5">
              <Shield className="w-3.5 h-3.5 mr-1.5" />SSO
            </TabsTrigger>
            <TabsTrigger value="sso-mapping" data-testid="sso-mapping-tab" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-xs px-3 py-1.5">
              <Link2 className="w-3.5 h-3.5 mr-1.5" />SSO-Mapping
            </TabsTrigger>

            {/* Separator */}
            <div className="w-px h-6 self-center bg-slate-300 mx-1" />

            {/* Verwaltung */}
            <TabsTrigger value="roles" data-testid="roles-tab" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-xs px-3 py-1.5">
              <Crown className="w-3.5 h-3.5 mr-1.5" />Rollen
            </TabsTrigger>
            <TabsTrigger value="credits" data-testid="credits-tab" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-xs px-3 py-1.5">
              <Coins className="w-3.5 h-3.5 mr-1.5" />Credits
            </TabsTrigger>

            {/* Separator */}
            <div className="w-px h-6 self-center bg-slate-300 mx-1" />

            {/* System */}
            <TabsTrigger value="system" data-testid="system-tab" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-xs px-3 py-1.5">
              <Activity className="w-3.5 h-3.5 mr-1.5" />System
            </TabsTrigger>
            <TabsTrigger value="db-lookup" data-testid="db-lookup-tab" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-xs px-3 py-1.5">
              <Database className="w-3.5 h-3.5 mr-1.5" />DB
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="logs-tab" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-xs px-3 py-1.5">
              <ScrollText className="w-3.5 h-3.5 mr-1.5" />Logs
            </TabsTrigger>
            <TabsTrigger value="backup" data-testid="backup-tab" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-xs px-3 py-1.5">
              <HardDrive className="w-3.5 h-3.5 mr-1.5" />Backup
            </TabsTrigger>

            {/* Separator */}
            <div className="w-px h-6 self-center bg-slate-300 mx-1" />

            <TabsTrigger value="docs" data-testid="docs-tab" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-xs px-3 py-1.5">
              <BookOpen className="w-3.5 h-3.5 mr-1.5" />Doku
            </TabsTrigger>
          </>)}
        </TabsList>

        {/* Theme Tab */}
        <TabsContent value="theme">
          <ThemeEditor />
        </TabsContent>

        {/* API Credentials Tab - Super-Admin Only */}
        {isSuperAdmin && (
        <TabsContent value="credentials" className="space-y-6">
          <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <CardHeader>
              <CardTitle className="text-xl font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>
                Zoho API Konfiguration
              </CardTitle>
              <CardDescription style={{ color: 'var(--color-text-secondary)' }}>
                Konfigurieren Sie die API-Credentials für Zoho Books, CRM und Desk
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleConfigureCredentials} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="service">Zoho Service</Label>
                  <select
                    id="service"
                    value={credentials.service}
                    onChange={(e) => setCredentials({ 
                      ...credentials, 
                      service: e.target.value,
                      redirect_uri: `${process.env.REACT_APP_BACKEND_URL}/api/oauth/${e.target.value}/callback`
                    })}
                    className="flex h-10 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
                    data-testid="service-select"
                  >
                    <option value="books">Zoho Books (Rechnungen)</option>
                    <option value="crm">Zoho CRM (Stammdaten)</option>
                    <option value="desk">Zoho Desk (Tickets)</option>
                    <option value="credits">Zoho Credits (CR-SKU System)</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_id">Client ID</Label>
                  <Input
                    id="client_id"
                    type="text"
                    value={credentials.client_id}
                    onChange={(e) => setCredentials({ ...credentials, client_id: e.target.value })}
                    placeholder="Ihre Zoho Client ID"
                    required
                    data-testid="client-id-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_secret">Client Secret</Label>
                  <Input
                    id="client_secret"
                    type="password"
                    value={credentials.client_secret}
                    onChange={(e) => setCredentials({ ...credentials, client_secret: e.target.value })}
                    placeholder="Ihr Zoho Client Secret"
                    required
                    data-testid="client-secret-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="redirect_uri">Redirect URI</Label>
                  <Input
                    id="redirect_uri"
                    type="text"
                    value={credentials.redirect_uri}
                    onChange={(e) => setCredentials({ ...credentials, redirect_uri: e.target.value })}
                    placeholder="http://localhost:8001/api/webhooks/callback"
                    required
                    data-testid="redirect-uri-input"
                  />
                  <p className="text-xs text-slate-500">
                    Diese URI muss in Ihrer Zoho App Konfiguration hinterlegt sein
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="save-credentials-button"
                  style={{
                    backgroundColor: 'var(--color-primary)',
                    color: 'var(--color-text-on-primary)'
                  }}
                  className="w-full hover:opacity-90"
                >
                  {loading ? 'Speichern...' : 'Credentials Speichern'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Credential Status Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {['books', 'crm', 'desk', 'credits'].map((service) => {
              const serviceLabels = {
                books: 'Books',
                crm: 'CRM',
                desk: 'Desk',
                credits: 'Credits'
              };
              
              const oauth = oauthStatus[service] || {};
              const isAuthorized = oauth.authorized;
              const isTokenExpired = oauth.token_expired;
              
              return (
                <Card 
                  key={service} 
                  style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
                  data-testid={`credential-status-${service}`}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg capitalize flex items-center justify-between" style={{ color: 'var(--color-text-primary)' }}>
                      Zoho {serviceLabels[service]}
                      {isAuthorized ? (
                        <Shield className="w-5 h-5" style={{ color: 'var(--color-success)' }} />
                      ) : credentialStatus[service]?.configured ? (
                        <CheckCircle className="w-5 h-5" style={{ color: '#f59e0b' }} />
                      ) : (
                        <XCircle className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {credentialStatus[service]?.configured ? (
                      <div className="space-y-3">
                        {/* Status Badges */}
                        <div className="flex flex-wrap gap-2">
                          <Badge style={{ backgroundColor: '#d1fae5', color: '#065f46', borderColor: '#86efac' }} className="border text-xs">
                            Konfiguriert
                          </Badge>
                          {isAuthorized ? (
                            <Badge style={{ backgroundColor: '#dbeafe', color: '#1e40af', borderColor: '#93c5fd' }} className="border text-xs">
                              Autorisiert
                            </Badge>
                          ) : (
                            <Badge style={{ backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fcd34d' }} className="border text-xs">
                              Nicht autorisiert
                            </Badge>
                          )}
                          {isAuthorized && isTokenExpired && (
                            <Badge style={{ backgroundColor: '#fee2e2', color: '#991b1b', borderColor: '#fca5a5' }} className="border text-xs">
                              Token abgelaufen
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          Client ID: {credentialStatus[service]?.client_id_preview}
                        </p>
                        
                        {/* OAuth Authorize Button */}
                        {!isAuthorized && (
                          <Button
                            size="sm"
                            onClick={() => handleAuthorizeOAuth(service)}
                            disabled={authorizingService === service}
                            data-testid={`authorize-oauth-${service}`}
                            className="w-full"
                            style={{
                              backgroundColor: 'var(--color-primary)',
                              color: 'var(--color-text-on-primary)'
                            }}
                          >
                            {authorizingService === service ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                Weiterleitung...
                              </>
                            ) : (
                              <>
                                <ExternalLink className="w-3 h-3 mr-2" />
                                Bei Zoho autorisieren
                              </>
                            )}
                          </Button>
                        )}
                        
                        {/* Token Refresh Button */}
                        {isAuthorized && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRefreshToken(service)}
                            disabled={refreshingService === service}
                            data-testid={`refresh-token-${service}`}
                            className="w-full"
                            style={{
                              borderColor: 'var(--color-primary)',
                              color: 'var(--color-primary)'
                            }}
                          >
                            {refreshingService === service ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                Erneuere...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-3 h-3 mr-2" />
                                Token erneuern
                              </>
                            )}
                          </Button>
                        )}
                        
                        {/* Test Connection Button */}
                        {isAuthorized && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleTestConnection(service)}
                            disabled={testingService === service}
                            data-testid={`test-connection-${service}`}
                            className="w-full"
                            style={{
                              borderColor: 'var(--color-primary)',
                              color: 'var(--color-primary)'
                            }}
                          >
                            {testingService === service ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                Teste...
                              </>
                            ) : (
                              <>
                                <Wifi className="w-3 h-3 mr-2" />
                                Verbindung testen
                              </>
                            )}
                          </Button>
                        )}
                        
                        {/* Test Result Display */}
                        {testResults[service] && (
                          <div className={`text-xs p-2 rounded ${testResults[service].success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                            {testResults[service].success ? (
                              <span className="flex items-center">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                {testResults[service].message}
                              </span>
                            ) : (
                              <span className="flex items-center">
                                <WifiOff className="w-3 h-3 mr-1" />
                                {testResults[service].message}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Badge style={{ backgroundColor: 'var(--color-background)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }} className="border">
                        Nicht konfiguriert
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
        )}

        {/* Webhook Tab - Super-Admin Only */}
        {isSuperAdmin && (
        <TabsContent value="webhook" className="space-y-6">
          <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <CardHeader>
              <CardTitle className="text-xl font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>
                Zoho Webhook Konfiguration
              </CardTitle>
              <CardDescription style={{ color: 'var(--color-text-secondary)' }}>
                Konfigurieren Sie den Webhook Secret für Signatur-Verifikation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleConfigureWebhook} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="webhook_secret">Webhook Secret</Label>
                  <Input
                    id="webhook_secret"
                    type="password"
                    value={webhookConfig.secret}
                    onChange={(e) => setWebhookConfig({ ...webhookConfig, secret: e.target.value })}
                    placeholder="Ihr Zoho Webhook Secret"
                    required
                    data-testid="webhook-secret-input"
                  />
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    Der Secret wird in Zoho Books unter Webhooks → Configure hinterlegt
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="verify_signature"
                    checked={webhookConfig.verify_signature}
                    onChange={(e) => setWebhookConfig({ ...webhookConfig, verify_signature: e.target.checked })}
                    className="rounded border-gray-300"
                    data-testid="verify-signature-checkbox"
                  />
                  <Label htmlFor="verify_signature" className="cursor-pointer" style={{ color: 'var(--color-text-primary)' }}>
                    Signatur-Verifikation aktivieren (empfohlen)
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="save-webhook-button"
                  style={{
                    backgroundColor: 'var(--color-primary)',
                    color: 'var(--color-text-on-primary)'
                  }}
                  className="w-full hover:opacity-90"
                >
                  {loading ? 'Speichern...' : 'Webhook-Konfiguration speichern'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Webhook Status */}
          <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center justify-between" style={{ color: 'var(--color-text-primary)' }}>
                Webhook Status
                {webhookStatus?.configured ? (
                  <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-success)' }} />
                ) : (
                  <XCircle className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {webhookStatus?.configured ? (
                <div className="space-y-2">
                  <Badge style={{ backgroundColor: '#d1fae5', color: '#065f46', borderColor: '#86efac' }} className="border">
                    Konfiguriert
                  </Badge>
                  <p className="text-xs mt-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Secret: {webhookStatus?.secret_preview}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    Signatur-Verifikation: {webhookStatus?.verify_signature ? 'Aktiviert' : 'Deaktiviert'}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    Konfiguriert am: {new Date(webhookStatus?.configured_at).toLocaleDateString('de-DE')}
                  </p>
                </div>
              ) : (
                <Badge style={{ backgroundColor: 'var(--color-background)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }} className="border">
                  Nicht konfiguriert
                </Badge>
              )}
            </CardContent>
          </Card>

          {/* Webhook URL Info */}
          <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <CardHeader>
              <CardTitle className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Webhook URL
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
                <code className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                  {process.env.REACT_APP_BACKEND_URL}/api/webhooks/payment-events
                </code>
              </div>
              <p className="text-xs mt-2" style={{ color: 'var(--color-text-secondary)' }}>
                Diese URL in Zoho Books → Settings → Webhooks → Configure hinterlegen
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* System Status Tab - Super-Admin Only */}
        {isSuperAdmin && (
        <TabsContent value="system" className="space-y-6">
          {systemHealth ? (
            <div className="space-y-6">
              {/* Status Header */}
              <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${systemHealth.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'}`} />
                      <div>
                        <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{systemHealth.status === 'healthy' ? 'System betriebsbereit' : 'System eingeschränkt'}</p>
                        <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{systemHealth.status_detail} | {new Date(systemHealth.timestamp).toLocaleString('de-DE')}</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchSystemHealth} data-testid="refresh-health">
                      <RefreshCw className="w-4 h-4 mr-2" />Aktualisieren
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Performance Metrics */}
              {systemHealth.performance && (
                <div className="grid md:grid-cols-3 gap-4">
                  {[
                    { label: 'CPU', value: `${systemHealth.performance.cpu_percent}%`, sub: `${systemHealth.performance.cpu_cores} Kerne | Load: ${systemHealth.performance.load_1m}`, pct: systemHealth.performance.cpu_percent },
                    { label: 'RAM', value: `${systemHealth.performance.ram_used_gb} / ${systemHealth.performance.ram_total_gb} GB`, sub: `${systemHealth.performance.ram_percent}% belegt`, pct: systemHealth.performance.ram_percent },
                    { label: 'Festplatte', value: `${systemHealth.performance.disk_used_gb} / ${systemHealth.performance.disk_total_gb} GB`, sub: `${systemHealth.performance.disk_percent}% belegt`, pct: systemHealth.performance.disk_percent },
                  ].map((m, i) => (
                    <Card key={i} style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
                      <CardContent className="pt-4">
                        <p className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>{m.label}</p>
                        <p className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{m.value}</p>
                        <p className="text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>{m.sub}</p>
                        <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${m.pct}%`, backgroundColor: m.pct > 80 ? '#ef4444' : m.pct > 60 ? '#f59e0b' : 'var(--color-primary)' }} />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Database + Uptime */}
              {systemHealth.database && (
                <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
                  <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--color-text-primary)' }}>Datenbank</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {[
                        { label: 'Status', value: <Badge className="bg-green-50 text-green-700 border-green-200 border">{systemHealth.database.status}</Badge> },
                        { label: 'Latenz', value: `${systemHealth.database.latency_ms} ms` },
                        { label: 'Collections', value: systemHealth.database.collections },
                        { label: 'Dokumente', value: systemHealth.database.documents?.toLocaleString('de-DE') },
                        { label: 'Speicher', value: `${systemHealth.database.storage_mb} MB` },
                      ].map((d, i) => (
                        <div key={i} className="text-center p-3 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
                          <p className="text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>{d.label}</p>
                          <p className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>{d.value}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Components */}
              {systemHealth.components && (
                <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
                  <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--color-text-primary)' }}>Komponenten</CardTitle></CardHeader>
                  <CardContent>
                    <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                      {systemHealth.components.map((c, i) => (
                        <div key={i} className="flex items-center justify-between py-3">
                          <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{c.name}</span>
                          <Badge className={`border text-xs ${c.status === 'ok' ? 'bg-green-50 text-green-700 border-green-200' : c.status === 'not_configured' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                            {c.status === 'ok' ? 'Aktiv' : c.status === 'not_configured' ? 'Nicht konfiguriert' : 'Fehler'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Business Data */}
              {systemHealth.business_data && (
                <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
                  <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--color-text-primary)' }}>Geschäftsdaten</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
                        <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Benutzer</p>
                        <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{systemHealth.business_data.users}</p>
                        <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{systemHealth.business_data.tenants} Mandant(en)</p>
                      </div>
                      <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
                        <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Rechnungen</p>
                        <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{systemHealth.business_data.invoices?.total}</p>
                        <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          <span className="text-green-600">{systemHealth.business_data.invoices?.paid} bezahlt</span>
                          {systemHealth.business_data.invoices?.overdue > 0 && <span className="text-red-600 ml-2">{systemHealth.business_data.invoices.overdue} überfällig</span>}
                        </p>
                      </div>
                      <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
                        <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Tickets</p>
                        <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{systemHealth.business_data.tickets?.total}</p>
                        <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          <span className="text-amber-600">{systemHealth.business_data.tickets?.open} offen</span>
                          <span className="text-green-600 ml-2">{systemHealth.business_data.tickets?.closed} geschlossen</span>
                        </p>
                      </div>
                      <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
                        <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Credits</p>
                        <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{systemHealth.business_data.credits?.total_balance}</p>
                        <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{systemHealth.business_data.credits?.users_with_balance} Benutzer mit Guthaben</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Active Users */}
              {systemHealth.active_users && systemHealth.active_users.length > 0 && (
                <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                      <Users className="w-4 h-4" /> Angemeldete Benutzer
                      {systemHealth.active_users_online > 0 && (
                        <Badge className="bg-green-50 text-green-700 border-green-200 border text-xs ml-2">
                          {systemHealth.active_users_online} online
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg border-slate-200 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Status</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Benutzer</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Rolle</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Mandant</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Letzter Login</th>
                          </tr>
                        </thead>
                        <tbody>
                          {systemHealth.active_users.map((u, i) => (
                            <tr key={i} className="border-b border-slate-100 last:border-0">
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <div className={`w-2.5 h-2.5 rounded-full ${u.status === 'online' ? 'bg-green-500' : u.status === 'recent' ? 'bg-amber-400' : 'bg-slate-300'}`} />
                                  <span className="text-xs text-slate-500">
                                    {u.status === 'online' ? 'Online' : u.status === 'recent' ? 'Kürzlich' : 'Inaktiv'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <p className="text-sm font-medium text-slate-800">{u.full_name || u.email}</p>
                                {u.full_name && <p className="text-xs text-slate-500">{u.email}</p>}
                              </td>
                              <td className="px-3 py-2">
                                <Badge className="text-xs border bg-slate-50 text-slate-600 border-slate-200">
                                  {u.global_role || u.role}
                                </Badge>
                              </td>
                              <td className="px-3 py-2 text-xs text-slate-600">{u.tenant_id}</td>
                              <td className="px-3 py-2 text-xs text-slate-500">
                                {u.minutes_ago !== null ? (
                                  u.minutes_ago < 1 ? 'Gerade eben' :
                                  u.minutes_ago < 60 ? `vor ${u.minutes_ago} Min.` :
                                  u.minutes_ago < 1440 ? `vor ${Math.round(u.minutes_ago / 60)} Std.` :
                                  `vor ${Math.round(u.minutes_ago / 1440)} Tagen`
                                ) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* System Info */}
              {systemHealth.performance && (
                <div className="text-xs text-center py-2" style={{ color: 'var(--color-text-secondary)' }}>
                  Python {systemHealth.performance.python} | Uptime: {systemHealth.performance.uptime_hours}h
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--color-primary)' }}></div>
            </div>
          )}
        </TabsContent>
        )}

        {/* SSO Tab - Super-Admin Only */}
        {isSuperAdmin && (
        <TabsContent value="sso" className="space-y-6">
          <SSOConfigPanel />
        </TabsContent>
        )}

        {/* Users Tab - Available for tenant_admin and above */}
        <TabsContent value="users" className="space-y-6">
          <UserManagementPanel />
        </TabsContent>

        {/* Roles Tab - Super-Admin Only */}
        {isSuperAdmin && (
        <TabsContent value="roles" className="space-y-6">
          <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <CardContent className="pt-6">
              <Tabs defaultValue="overview" className="space-y-4">
                <TabsList style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border)' }} className="border p-1">
                  <TabsTrigger 
                    value="overview"
                    className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    Rollenübersicht
                  </TabsTrigger>
                  <TabsTrigger 
                    value="assign"
                    className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    Benutzer-Rollen
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="overview">
                  <RolesPanel />
                </TabsContent>
                <TabsContent value="assign">
                  <UserRolesPanel />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* SSO Mapping Tab - Super-Admin Only */}
        {isSuperAdmin && (
        <TabsContent value="sso-mapping" className="space-y-6">
          <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <CardContent className="pt-6">
              <SSOMapppingPanel />
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* DB Lookup Tab - Super-Admin Only */}
        {isSuperAdmin && (
        <TabsContent value="db-lookup" className="space-y-6">
          <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <CardContent className="pt-6">
              <DbLookupPanel />
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Logs Tab - Super-Admin Only */}
        {isSuperAdmin && (
        <TabsContent value="logs" className="space-y-6">
          <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <CardContent className="pt-6">
              <LogsPanel />
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Backup Tab - Super-Admin Only */}
        {isSuperAdmin && (
        <TabsContent value="backup" className="space-y-6">
          <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <CardContent className="pt-6">
              <BackupPanel />
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Credits Tab - Super-Admin Only */}
        {isSuperAdmin && (
        <TabsContent value="credits" className="space-y-6">
          <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <CardContent className="pt-6">
              <AdminCreditsPanel />
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Email Config Tab - Super-Admin Only */}
        {isSuperAdmin && (
        <TabsContent value="email" className="space-y-6">
          <EmailConfigPanel />
        </TabsContent>
        )}

        {/* Documentation Tab - Super-Admin Only */}
        {isSuperAdmin && (
        <TabsContent value="docs" className="space-y-6">
          <DocumentationPanel />
        </TabsContent>
        )}
      </Tabs>
    </motion.div>
    )
  );
};